import {
  ACTIVITY_PATH,
  GENERATION_INSTRUCTIONS_PATH,
  type GenerationInstructionsResource,
  type SaveGenerationInstructionsRequest,
  STORE_DESIGNS_PATH,
  type CreateStoreDesignRequest,
  type StoreDesignResource,
  type StoreDesignDetail,
  type StoreDesignGeneration,
  type StoreDesignGenerationResult,
  type GenerateStoreDesignRequest,
  type SelectStoreDesignCandidateRequest,
  APPS_PATH,
  ARTIFACTS_PATH,
  BULK_RUNS_PATH,
  COSTS_PATH,
  CANDIDATES_PATH,
  LEARNINGS_PATH,
  LIBRARY_ITEMS_PATH,
  LISTING_SNAPSHOTS_PATH,
  INVENTORY_PATH,
  STORE_LISTINGS_PATH,
  TRACKER_IMPORTS_PATH,
  LEARNING_NARROWINGS_PATH,
  OUTCOME_EVIDENCE_PATH,
  PROVIDERS_PATH,
  CANDIDATE_SLOTS_PATH,
  CSRF_COOKIE,
  CSRF_HEADER,
  IDEMPOTENCY_HEADER,
  EXPERIMENTS_PATH,
  ErrorResponseSchema,
  SCREENSHOT_ITEMS_PATH,
  type ActivitySnapshot,
  type AppCreatedResponse,
  type AppListResponse,
  type AppProfileResponse,
  type AppResource,
  type ArtifactResource,
  type BranchRequest,
  type CandidateBatchResource,
  type CandidateFlagsRequest,
  type CandidateResource,
  type CompositionEditRequest,
  type CompositionResource,
  type CreateAppRequest,
  type CreateExperimentRequest,
  type EditSlideRequest,
  type ExperimentDetail,
  type ExperimentResource,
  type GenerationAttemptResource,
  type IngestReferenceResponse,
  type InventoryResponse,
  type RegisterStoreListingsRequest,
  type RegisterStoreListingsResponse,
  type SetStoreListingLanguageRequest,
  type StartScanResponse,
  type StoreListingResource,
  type TrackerImportReport,
  type LibraryItemResource,
  type LibraryListResponse,
  type LibraryRole,
  type ListingSnapshotListResponse,
  type ListingSnapshotResource,
  type ReviewListingSnapshotRequest,
  type UpdateAppProfileRequest,
  type ScreenshotItemResource,
  type SessionResponse,
  type SlideEditResponse,
  type StoryPlanResource,
  type StoryBeatResource,
  type ExportPackageKind,
  type ExportPreviewResource,
  type PlayTestPairResource,
  type ProviderDiagnosticsResponse,
  type ProviderKeyResource,
  type ReviewResource,
  type SetPlayTestPairSlotRequest,
  type BulkPreflightRequest,
  type BulkPreflightResource,
  type BulkRunDetail,
  type BulkRunListResponse,
  type ContinueWithAllowanceRequest,
  type CostSummaryResource,
  type LaunchBulkRunRequest,
  type BackdateRequest,
  type CreativeLearningResource,
  type LearningNarrowingResource,
  type LearningSuggestionResource,
  type NarrowingDecision,
  type LifecycleResource,
  type OutcomeEvidenceInput,
  type OutcomeEvidenceResource,
  type OutcomeFactResource,
  type OutcomeResource,
  type RecordOutcomeRequest,
  type ReplaceControlRequest,
  type UpdateExperimentRequest,
  type UpdateLearningRequest,
  type WorkAccepted,
  type AdaptOrientationRequest,
  type EditSceneRequest,
  type RenderVideoResponse,
  type SaveVideoDeliveryRequest,
  type VideoCandidateResource,
  type VideoCoverPreviewResource,
  type VideoDeliveryResource,
  type VideoEditResponse,
  type VideoShelfResource,
  adaptOrientationPath,
  videoCandidatePath,
  videoCoverPreviewPath,
  videoDeliveryPath,
  videoRendersPath,
  videoSceneEditPath,
  videoSceneOrderPath,
  videoShelfPath,
  experimentControlPath,
  experimentExportPackagesPath,
  experimentOutcomePath,
  compositionEditPath,
  compositionPath,
  exportPreviewPath,
  ignoredLearningsPath,
  lifecyclePath,
  markLivePath,
  outcomeEvidencePath,
  playTestPairPath,
  reviewPath,
  safeFixPath,
  providerKeyPath,
  IMAGE_SOURCE_PATH,
} from '@studio/shared';

function csrfToken(): string {
  const entry = document.cookie.split('; ').find((pair) => pair.startsWith(`${CSRF_COOKIE}=`));
  return entry === undefined ? '' : decodeURIComponent(entry.slice(CSRF_COOKIE.length + 1));
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let unauthorizedHandler: (() => void) | null = null;

/**
 * One place learns that the session ended. Every call goes through `request`, so a 401
 * from any of them - not only the session probe - puts the shell back on the password
 * screen. The URL is never touched, which is what lets the operator land back on the
 * route they were interrupted on.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * How long one press stays one press.
 *
 * A command that buys generation, branches a Candidate, records an Outcome or builds an
 * Export Package carries an idempotency key, and a second press of the same button with
 * the same body reuses it: the server recognises the replay and returns what the first
 * press created instead of buying it twice. The window covers a double-click and the
 * moment a slow round trip leaves the button live; a deliberate press later mints a
 * new key and is a new command.
 */
const IDEMPOTENCY_WINDOW_MS = 4000;

interface HeldKey {
  readonly key: string;
  expires: number;
  inFlight: number;
}

const heldKeys = new Map<string, HeldKey>();

function mintKey(): string {
  const generated: string | undefined = globalThis.crypto?.randomUUID?.();
  return generated ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function holdKey(signature: string): string {
  const now = Date.now();
  const standing = heldKeys.get(signature);
  if (standing !== undefined && (standing.inFlight > 0 || standing.expires > now)) {
    standing.inFlight += 1;
    return standing.key;
  }
  const fresh: HeldKey = { key: mintKey(), expires: now + IDEMPOTENCY_WINDOW_MS, inFlight: 1 };
  heldKeys.set(signature, fresh);
  return fresh.key;
}

function releaseKey(signature: string): void {
  const standing = heldKeys.get(signature);
  if (standing === undefined) return;
  standing.inFlight = Math.max(0, standing.inFlight - 1);
  standing.expires = Date.now() + IDEMPOTENCY_WINDOW_MS;
}

/** The shelf and whatever the Studio has proposed about it, read together. */
export interface LearningShelfResponse {
  readonly learnings: readonly CreativeLearningResource[];
  readonly narrowings?: readonly LearningNarrowingResource[];
}

/** A command produces paid work, a branch, an Outcome or an export. A read does not. */
interface RequestOptions {
  readonly command?: boolean;
}

async function request<T>(method: string, path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && method !== 'HEAD') headers[CSRF_HEADER] = csrfToken();

  const signature = options.command === true ? `${method} ${path} ${JSON.stringify(body ?? null)}` : null;
  if (signature !== null) headers[IDEMPOTENCY_HEADER] = holdKey(signature);
  try {
    return await send<T>(method, path, body, headers);
  } finally {
    if (signature !== null) releaseKey(signature);
  }
}

async function send<T>(method: string, path: string, body: unknown, headers: Record<string, string>): Promise<T> {

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401) unauthorizedHandler?.();
  if (response.status === 204) return undefined as T;

  const payload: unknown =
    response.headers.get('content-type')?.includes('application/json') === true ? await response.json() : null;
  if (!response.ok) {
    const parsed = ErrorResponseSchema.safeParse(payload);
    if (parsed.success) throw new ApiError(response.status, parsed.data.error.message);
    const error = payload !== null && typeof payload === 'object' && 'error' in payload ? payload.error : undefined;
    const fallback = error !== null && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
      ? error.message : null;
    throw new ApiError(response.status, fallback ?? `Запрос не выполнен (${response.status}).`);
  }
  return payload as T;
}

/**
 * Every surface reads and writes through this one object. The DTOs are the ones the
 * server publishes in `@studio/shared`; nothing here invents a shape of its own, and
 * `Origin` plus the CSRF header are the transport's business, not each caller's.
 */
export const api = {
  readGenerationInstructions: (): Promise<GenerationInstructionsResource> => request('GET', GENERATION_INSTRUCTIONS_PATH),
  saveGenerationInstructions: (body: SaveGenerationInstructionsRequest): Promise<GenerationInstructionsResource> =>
    request('PUT', GENERATION_INSTRUCTIONS_PATH, body),
  listStoreDesigns: (appId?: string): Promise<{ designs: readonly StoreDesignResource[] }> =>
    request('GET', appId === undefined ? STORE_DESIGNS_PATH : `${STORE_DESIGNS_PATH}?appId=${encodeURIComponent(appId)}`),
  readStoreDesignGeneration: (): Promise<StoreDesignGeneration> => request('GET', `${STORE_DESIGNS_PATH}/generation`),
  createStoreDesign: (body: CreateStoreDesignRequest): Promise<StoreDesignResource> =>
    request('POST', STORE_DESIGNS_PATH, body, { command: true }),
  readStoreDesign: (id: string): Promise<StoreDesignDetail> => request('GET', `${STORE_DESIGNS_PATH}/${id}`),
  selectStoreDesignCandidate: (id: string, body: SelectStoreDesignCandidateRequest): Promise<StoreDesignResource> =>
    request('PUT', `${STORE_DESIGNS_PATH}/${id}/selection`, body),
  generateStoreDesign: (id: string, body: GenerateStoreDesignRequest): Promise<StoreDesignGenerationResult> =>
    request('POST', `${STORE_DESIGNS_PATH}/${id}/generate`, body, { command: true }),
  requestStoreDesignExport: (id: string, previousExportId: string | null): Promise<{ jobId: string }> =>
    request('POST', `${STORE_DESIGNS_PATH}/${id}/exports`, { previousExportId }, { command: true }),
  readSession: (): Promise<SessionResponse> => request<SessionResponse>('GET', '/api/v1/session'),
  signIn: (password: string): Promise<SessionResponse> =>
    request<SessionResponse>('POST', '/api/v1/session', { password }),
  signOut: (): Promise<void> => request<void>('DELETE', '/api/v1/session'),
  /**
   * The snapshot half of the reconnect protocol, read here rather than by a `fetch` of
   * the activity client's own. It is an authenticated read like any other, so a session
   * that ended has to be able to say so through the one place that hears a 401 - the
   * indicator is background work, and background work that is refused in silence is how
   * a tab comes to show yesterday's Workspace without a word.
   */
  readActivity: (): Promise<ActivitySnapshot> => request<ActivitySnapshot>('GET', ACTIVITY_PATH),

  listApps: (): Promise<AppListResponse> => request<AppListResponse>('GET', APPS_PATH),
  createApp: (body: CreateAppRequest): Promise<AppCreatedResponse> =>
    request<AppCreatedResponse>('POST', APPS_PATH, body, { command: true }),
  readAppProfile: (appId: string): Promise<AppProfileResponse> =>
    request<AppProfileResponse>('GET', `${APPS_PATH}/${appId}/profile`),
  /** Out of every list of Apps and of `Scan all`, with everything the App has kept. */
  archiveApp: (appId: string): Promise<AppResource> => request<AppResource>('POST', `${APPS_PATH}/${appId}/archive`, {}),
  restoreApp: (appId: string): Promise<AppResource> => request<AppResource>('POST', `${APPS_PATH}/${appId}/restore`, {}),
  readLibrary: (appId: string): Promise<LibraryListResponse> =>
    request<LibraryListResponse>('GET', `${APPS_PATH}/${appId}/library`),
  ingestReference: (appId: string, url: string, role?: LibraryRole): Promise<IngestReferenceResponse> =>
    request<IngestReferenceResponse>('POST', `${APPS_PATH}/${appId}/references`, { url, role }, { command: true }),
  registerLibraryItem: (appId: string, artifactId: string, originName?: string): Promise<LibraryItemResource> =>
    request<LibraryItemResource>('POST', `${APPS_PATH}/${appId}/library`, { artifactId, originName }),
  /** Bytes go through the Artifact seam first; the Library entry names them afterwards. */
  uploadArtifact: async (file: File): Promise<ArtifactResource> => {
    const response = await fetch(`${ARTIFACTS_PATH}?fileName=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { [CSRF_HEADER]: csrfToken(), 'content-type': file.type === '' ? 'application/octet-stream' : file.type },
      body: file,
    });
    if (response.status === 401) unauthorizedHandler?.();
    if (!response.ok) throw new ApiError(response.status, 'Не удалось сохранить эти данные.');
    return (await response.json()) as ArtifactResource;
  },
  readSnapshots: (appId: string): Promise<ListingSnapshotListResponse> =>
    request<ListingSnapshotListResponse>('GET', `${APPS_PATH}/${appId}/listing-snapshots`),
  readSnapshot: (snapshotId: string): Promise<ListingSnapshotResource> =>
    request<ListingSnapshotResource>('GET', `${LISTING_SNAPSHOTS_PATH}/${snapshotId}`),
  /** A scan never rewrites a snapshot; it writes new readings beside the old ones. */
  startScan: (appId: string): Promise<StartScanResponse> =>
    request<StartScanResponse>('POST', `${APPS_PATH}/${appId}/scans`, {}, { command: true }),
  startListingScan: (storeListingId: string): Promise<StartScanResponse> =>
    request<StartScanResponse>('POST', `${STORE_LISTINGS_PATH}/${storeListingId}/scans`, {}, { command: true }),
  scanAll: (): Promise<StartScanResponse> => request<StartScanResponse>('POST', `${INVENTORY_PATH}/scans`, {}, { command: true }),
  readInventory: (): Promise<InventoryResponse> => request<InventoryResponse>('GET', INVENTORY_PATH),
  /**
   * One Tracker Import: the Studio reads the Tracker's catalog and creates the Apps and
   * Custom Store Listings it does not have. The answer is the report of what it did.
   */
  importFromTracker: (): Promise<TrackerImportReport> =>
    request<TrackerImportReport>('POST', TRACKER_IMPORTS_PATH, undefined, { command: true }),
  readStoreListings: (appId: string): Promise<{ listings: readonly StoreListingResource[] }> =>
    request<{ listings: readonly StoreListingResource[] }>('GET', `${APPS_PATH}/${appId}/store-listings`),
  registerStoreListings: (appId: string, body: RegisterStoreListingsRequest): Promise<RegisterStoreListingsResponse> =>
    request<RegisterStoreListingsResponse>('POST', `${APPS_PATH}/${appId}/store-listings`, body),
  setStoreListingLanguage: (storeListingId: string, body: SetStoreListingLanguageRequest): Promise<StoreListingResource> =>
    request<StoreListingResource>('PUT', `${STORE_LISTINGS_PATH}/${storeListingId}/language`, body),
  archiveStoreListing: (storeListingId: string): Promise<StoreListingResource> =>
    request<StoreListingResource>('POST', `${STORE_LISTINGS_PATH}/${storeListingId}/archive`, {}),
  /**
   * The review of an imported snapshot: what is left out, what order the rest is in,
   * what was replaced and what was added. It writes a new reviewed snapshot rather
   * than editing the imported one, which is why it is a command and not a PATCH.
   */
  reviewSnapshot: (snapshotId: string, body: ReviewListingSnapshotRequest): Promise<ListingSnapshotResource> =>
    request<ListingSnapshotResource>('POST', `${LISTING_SNAPSHOTS_PATH}/${snapshotId}/review`, body, { command: true }),
  /** The inferred role, corrected by the operator. `roleInferred` goes false with it. */
  setLibraryRole: (libraryItemId: string, role: LibraryRole): Promise<LibraryItemResource> =>
    request<LibraryItemResource>('PATCH', `${LIBRARY_ITEMS_PATH}/${libraryItemId}`, { role }),
  /** Standing App Profile material. Standing is still not selection into a Brief. */
  markStanding: (appId: string, libraryItemId: string): Promise<LibraryItemResource> =>
    request<LibraryItemResource>('POST', `${APPS_PATH}/${appId}/profile/materials`, { libraryItemId }),
  clearStanding: (appId: string, libraryItemId: string): Promise<LibraryItemResource> =>
    request<LibraryItemResource>('DELETE', `${APPS_PATH}/${appId}/profile/materials/${libraryItemId}`),
  /** Reusable identity and the standing constraints every Experiment of this App gets. */
  updateAppProfile: (appId: string, patch: UpdateAppProfileRequest): Promise<AppProfileResponse> =>
    request<AppProfileResponse>('PATCH', `${APPS_PATH}/${appId}/profile`, patch),

  listExperiments: (appId?: string): Promise<{ experiments: readonly ExperimentResource[] }> =>
    request<{ experiments: readonly ExperimentResource[] }>(
      'GET',
      appId === undefined ? EXPERIMENTS_PATH : `${EXPERIMENTS_PATH}?appId=${encodeURIComponent(appId)}`,
    ),
  createExperiment: (body: CreateExperimentRequest): Promise<ExperimentResource> =>
    request<ExperimentResource>('POST', EXPERIMENTS_PATH, body, { command: true }),
  readExperiment: (id: string): Promise<ExperimentDetail> =>
    request<ExperimentDetail>('GET', `${EXPERIMENTS_PATH}/${id}`),
  updateExperiment: (id: string, patch: UpdateExperimentRequest): Promise<ExperimentResource> =>
    request<ExperimentResource>('PATCH', `${EXPERIMENTS_PATH}/${id}`, patch),
  /** `Generate` and `Explore 6 more` are the same command with a different kind. */
  spread: (id: string, kind: 'initial' | 'explore'): Promise<CandidateBatchResource> =>
    request<CandidateBatchResource>('POST', `${EXPERIMENTS_PATH}/${id}/spread`, { kind }, { command: true }),
  useLearning: (id: string, learning: { learningId: string; text: string }): Promise<ExperimentResource> =>
    request<ExperimentResource>('POST', `${EXPERIMENTS_PATH}/${id}/learnings`, learning),
  unuseLearning: (id: string, learningId: string): Promise<ExperimentResource> =>
    request<ExperimentResource>('DELETE', `${EXPERIMENTS_PATH}/${id}/learnings/${learningId}`),

  /**
   * One Candidate on its own. A destination Brief reads the Candidate `Repeat for...`
   * carried in, and that Candidate belongs to a different Experiment, so the Experiment
   * detail this surface already has cannot answer for it.
   */
  readCandidate: (id: string): Promise<CandidateResource> =>
    request<CandidateResource>('GET', `${CANDIDATES_PATH}/${id}`),
  setCandidateFlags: (id: string, flags: CandidateFlagsRequest): Promise<CandidateResource> =>
    request<CandidateResource>('PATCH', `${CANDIDATES_PATH}/${id}`, flags),
  branchCandidate: (id: string, body: BranchRequest): Promise<CandidateBatchResource> =>
    request<CandidateBatchResource>('POST', `${CANDIDATES_PATH}/${id}/branch`, body, { command: true }),
  setStoryPlan: (id: string, beats: readonly StoryBeatResource[]): Promise<StoryPlanResource> =>
    request<StoryPlanResource>('PUT', `${CANDIDATES_PATH}/${id}/story-plan`, { beats }),
  reorderItems: (id: string, itemIds: readonly string[]): Promise<{ items: readonly ScreenshotItemResource[] }> =>
    request<{ items: readonly ScreenshotItemResource[] }>('PATCH', `${CANDIDATES_PATH}/${id}/screenshot-items/order`, {
      itemIds,
    }),
  editSlide: (itemId: string, body: EditSlideRequest): Promise<SlideEditResponse> =>
    request<SlideEditResponse>('POST', `${SCREENSHOT_ITEMS_PATH}/${itemId}/edit`, body, { command: true }),

  /** The Composition bench: the pieces the Studio inferred this single image is made of. */
  readComposition: (candidateId: string): Promise<CompositionResource> =>
    request<CompositionResource>('GET', compositionPath(candidateId)),
  /** `Create edited Candidate`. Until this, a nudge never left the browser. */
  createEditedCandidate: (candidateId: string, body: CompositionEditRequest): Promise<CandidateBatchResource> =>
    request<CandidateBatchResource>('POST', compositionEditPath(candidateId), body, { command: true }),

  /** Contact sheet, Play stage and Packing desk read one resource: they are one pass. */
  readReview: (experimentId: string): Promise<ReviewResource> => request<ReviewResource>('GET', reviewPath(experimentId)),
  /** The whole repair sequence, in one press. It is never applied a step at a time. */
  applySafeFix: (candidateId: string): Promise<WorkAccepted> =>
    request<WorkAccepted>('POST', safeFixPath(candidateId), {}, { command: true }),
  setPlayTestPairSlot: (experimentId: string, body: SetPlayTestPairSlotRequest): Promise<PlayTestPairResource> =>
    request<PlayTestPairResource>('PUT', `${playTestPairPath(experimentId)}/slots`, body),
  clearPlayTestPairSlot: (experimentId: string, slotIndex: number): Promise<PlayTestPairResource> =>
    request<PlayTestPairResource>('DELETE', `${playTestPairPath(experimentId)}/slots/${slotIndex}`),
  readExportPreview: (experimentId: string, kind: ExportPackageKind): Promise<ExportPreviewResource> =>
    request<ExportPreviewResource>('GET', exportPreviewPath(experimentId, kind)),
  requestExportPackage: (experimentId: string, kind: ExportPackageKind): Promise<{ jobId: string }> =>
    request<{ jobId: string }>('POST', experimentExportPackagesPath(experimentId), { kind }, { command: true }),

  /** The Learn stage reads in one call and every command answers with the same shape. */
  readLifecycle: (experimentId: string): Promise<LifecycleResource> =>
    request<LifecycleResource>('GET', lifecyclePath(experimentId)),
  markLive: (experimentId: string): Promise<LifecycleResource> =>
    request<LifecycleResource>('POST', markLivePath(experimentId), {}, { command: true }),
  replaceControl: (experimentId: string, body: ReplaceControlRequest): Promise<LifecycleResource> =>
    request<LifecycleResource>('PUT', experimentControlPath(experimentId), body),
  /** Closing. One enum is the whole requirement. */
  recordOutcome: (experimentId: string, body: RecordOutcomeRequest): Promise<LifecycleResource> =>
    request<LifecycleResource>('POST', experimentOutcomePath(experimentId), body, { command: true }),
  addOutcomeEvidence: (experimentId: string, body: OutcomeEvidenceInput): Promise<OutcomeResource> =>
    request<OutcomeResource>('POST', outcomeEvidencePath(experimentId), body, { command: true }),
  updateOutcomeEvidence: (evidenceId: string, facts: readonly OutcomeFactResource[]): Promise<OutcomeEvidenceResource> =>
    request<OutcomeEvidenceResource>('PATCH', `${OUTCOME_EVIDENCE_PATH}/${evidenceId}`, { facts }),
  backdateLifecycle: (experimentId: string, body: BackdateRequest): Promise<LifecycleResource> =>
    request<LifecycleResource>('PATCH', lifecyclePath(experimentId), body),

  /** The App page shelf: this App's own Creative Learning and no other App's. */
  readLearnings: (appId: string, includeRetired = false): Promise<LearningShelfResponse> =>
    request<LearningShelfResponse>(
      'GET',
      `${LEARNINGS_PATH}?appId=${encodeURIComponent(appId)}${includeRetired ? '&includeRetired=true' : ''}`,
    ),
  /** Answering a proposed narrower reading. Nothing is applied without one of the three. */
  resolveNarrowing: (narrowingId: string, decision: NarrowingDecision, appId: string): Promise<LearningShelfResponse> =>
    request<LearningShelfResponse>(
      'POST',
      `${LEARNING_NARROWINGS_PATH}/${narrowingId}?appId=${encodeURIComponent(appId)}`,
      { decision },
      { command: true },
    ),
  /** Beside a Brief: this App first, then a few labelled cross-App suggestions. */
  readSuggestions: (experimentId: string): Promise<{ readonly suggestions: readonly LearningSuggestionResource[] }> =>
    request<{ readonly suggestions: readonly LearningSuggestionResource[] }>(
      'GET',
      `${LEARNINGS_PATH}?experimentId=${encodeURIComponent(experimentId)}`,
    ),
  updateLearning: (learningId: string, body: UpdateLearningRequest): Promise<CreativeLearningResource> =>
    request<CreativeLearningResource>('PATCH', `${LEARNINGS_PATH}/${learningId}`, body),
  /** Within this Experiment only. Nothing outside it changes. */
  ignoreLearning: (
    experimentId: string,
    learningId: string,
  ): Promise<{ readonly suggestions: readonly LearningSuggestionResource[] }> =>
    request<{ readonly suggestions: readonly LearningSuggestionResource[] }>(
      'POST',
      ignoredLearningsPath(experimentId),
      { learningId },
      { command: true },
    ),
  unignoreLearning: (
    experimentId: string,
    learningId: string,
  ): Promise<{ readonly suggestions: readonly LearningSuggestionResource[] }> =>
    request<{ readonly suggestions: readonly LearningSuggestionResource[] }>(
      'DELETE',
      `${ignoredLearningsPath(experimentId)}/${learningId}`,
    ),

  /** Read-only, and never a value: states and variable names. */
  readProviders: (): Promise<ProviderDiagnosticsResponse> => request<ProviderDiagnosticsResponse>('GET', PROVIDERS_PATH),
  /** The one read that carries a key value, made only when the operator asks to see it. */
  readProviderKey: (name: string): Promise<ProviderKeyResource> => request<ProviderKeyResource>('GET', providerKeyPath(name)),
  saveProviderKey: (name: string, value: string): Promise<ProviderDiagnosticsResponse> =>
    request<ProviderDiagnosticsResponse>('PUT', providerKeyPath(name), { value }),
  removeProviderKey: (name: string): Promise<ProviderDiagnosticsResponse> =>
    request<ProviderDiagnosticsResponse>('DELETE', providerKeyPath(name)),
  chooseImageSource: (providerId: string | null): Promise<ProviderDiagnosticsResponse> =>
    request<ProviderDiagnosticsResponse>('PUT', IMAGE_SOURCE_PATH, { providerId }),

  /** The video shelf: every Video Candidate of this Experiment with its Animatic. */
  readVideoShelf: (experimentId: string): Promise<VideoShelfResource> =>
    request<VideoShelfResource>('GET', videoShelfPath(experimentId)),
  readVideoCandidate: (candidateId: string): Promise<VideoCandidateResource> =>
    request<VideoCandidateResource>('GET', videoCandidatePath(candidateId)),
  /** Reordering the story changes what it says, so it answers with a descendant. */
  reorderScenes: (candidateId: string, sceneIds: readonly string[]): Promise<VideoEditResponse> =>
    request<VideoEditResponse>('POST', videoSceneOrderPath(candidateId), { sceneIds }, { command: true }),
  editScene: (sceneId: string, body: EditSceneRequest): Promise<VideoEditResponse> =>
    request<VideoEditResponse>('POST', videoSceneEditPath(sceneId), body, { command: true }),
  adaptOrientation: (candidateId: string, body: AdaptOrientationRequest = {}): Promise<VideoEditResponse> =>
    request<VideoEditResponse>('POST', adaptOrientationPath(candidateId), body, { command: true }),
  /** One, several or all selected, straight from the shelf. Keep is not a gate. */
  renderVideos: (experimentId: string, candidateIds: readonly string[]): Promise<RenderVideoResponse> =>
    request<RenderVideoResponse>('POST', videoRendersPath(experimentId), { candidateIds }, { command: true }),
  saveVideoDelivery: (candidateId: string, body: SaveVideoDeliveryRequest): Promise<VideoDeliveryResource> =>
    request<VideoDeliveryResource>('PUT', videoDeliveryPath(candidateId), body),
  readVideoCoverPreview: (experimentId: string, libraryItemId?: string): Promise<VideoCoverPreviewResource> =>
    request<VideoCoverPreviewResource>('GET', videoCoverPreviewPath(experimentId, libraryItemId)),

  /**
   * A Bulk Run. The preflight is a POST because a matrix does not fit in a query
   * string, and a read because it writes nothing: no Experiment and no money exist
   * until the launch below.
   */
  bulkPreflight: (body: BulkPreflightRequest): Promise<BulkPreflightResource> =>
    request<BulkPreflightResource>('POST', `${BULK_RUNS_PATH}/preflight`, body),
  launchBulkRun: (body: LaunchBulkRunRequest): Promise<BulkRunDetail> =>
    request<BulkRunDetail>('POST', BULK_RUNS_PATH, body, { command: true }),
  listBulkRuns: (): Promise<BulkRunListResponse> => request<BulkRunListResponse>('GET', BULK_RUNS_PATH),
  readBulkRun: (id: string): Promise<BulkRunDetail> => request<BulkRunDetail>('GET', `${BULK_RUNS_PATH}/${id}`),
  /** `Run again`: the matrix back in an editable preflight, priced as of now. */
  rerunBulkRun: (id: string): Promise<BulkPreflightResource> =>
    request<BulkPreflightResource>('GET', `${BULK_RUNS_PATH}/${id}/rerun`),
  pauseBulkRun: (id: string): Promise<BulkRunDetail> =>
    request<BulkRunDetail>('POST', `${BULK_RUNS_PATH}/${id}/pause`, {}, { command: true }),
  resumeBulkRun: (id: string): Promise<BulkRunDetail> =>
    request<BulkRunDetail>('POST', `${BULK_RUNS_PATH}/${id}/resume`, {}, { command: true }),
  continueBulkRun: (id: string, body: ContinueWithAllowanceRequest = {}): Promise<BulkRunDetail> =>
    request<BulkRunDetail>('POST', `${BULK_RUNS_PATH}/${id}/continue-with-allowance`, body, { command: true }),
  stopBulkRun: (id: string): Promise<BulkRunDetail> =>
    request<BulkRunDetail>('POST', `${BULK_RUNS_PATH}/${id}/stop-remaining`, {}, { command: true }),
  retryBulkRun: (id: string): Promise<BulkRunDetail> =>
    request<BulkRunDetail>('POST', `${BULK_RUNS_PATH}/${id}/retry-failed`, {}, { command: true }),
  /** Compact spend: an Experiment, a Bulk Run, or a period of the Workspace. */
  readCosts: (query: { experimentId?: string; bulkRunId?: string; from?: string; to?: string }): Promise<CostSummaryResource> => {
    const search = new URLSearchParams(
      Object.entries(query).filter((entry): entry is [string, string] => entry[1] !== undefined),
    ).toString();
    return request<CostSummaryResource>('GET', search === '' ? COSTS_PATH : `${COSTS_PATH}?${search}`);
  },

  retrySlot: (slotId: string): Promise<{ jobId: string }> =>
    request<{ jobId: string }>('POST', `${CANDIDATE_SLOTS_PATH}/${slotId}/retry`, {}, { command: true }),
  readAttempts: (slotId: string): Promise<{ attempts: readonly GenerationAttemptResource[] }> =>
    request<{ attempts: readonly GenerationAttemptResource[] }>('GET', `${CANDIDATE_SLOTS_PATH}/${slotId}/attempts`),
};
